import { motion } from "framer-motion";
import { Database, Radio, Disc3, Linkedin } from "lucide-react";

const CREDS = [
  {
    img: "/assets/aidar.png",
    contain: true,
    kicker: "DATA VERIFIED BY",
    title: "AIDAR.AI",
    body: "Our analytics backbone. Every move we make is read, measured, and verified against real market data.",
    id: "aidar",
  },
  {
    img: "/assets/tim-ortiz.jpg",
    pos: "object-center",
    kicker: "PERSONALLY TRAINED + MENTORED BY",
    title: "TIM ORTIZ // EVENTVIBE.COM",
    body: "Mentored one-on-one by a live-music industry veteran. The playbook is learned, not guessed.",
    id: "eventvibe",
  },
  {
    img: "/assets/danny-hamilton.jpg",
    pos: "object-center",
    kicker: "IN THE ROOM WITH",
    title: "DANNY HAMILTON // Y.D.B. — WU-TANG CLAN",
    body: "We work alongside the manager of Y.D.B. from Wu-Tang Clan. Relationships this deep can't be bought.",
    id: "wutang",
  },
  {
    img: "/assets/le-affaire.png",
    kicker: "ON SCREEN WITH",
    title: "LE AFFAIRE MUSIC TV",
    body: "Music entertainment television — carrying our artists from the airwaves to the screen.",
    id: "le-affaire",
  },
  {
    img: "/assets/chad-focus.jpg",
    kicker: "MENTOR // CHADFOCUS.COM",
    title: "CHAD FOCUS",
    body: "Artist, mentor, and strategist who sharpened how we move in the independent grind.",
    url: "https://www.chadfocus.com/",
    id: "chad-focus",
  },
  {
    img: "/assets/lx-gibran.jpg",
    kicker: "IN THE ROOM WITH",
    title: "LX GIBRAN",
    body: "Dance music legend out of Mexico. Decades of club wisdom on call.",
    id: "lx-gibran",
  },
];

export default function Credibility() {
  return (
    <section id="credibility" className="relative py-28 sm:py-36 bg-[#0C0C10] border-y border-[#22222B]" data-testid="credibility-section">
      <div className="max-w-7xl mx-auto px-5 sm:px-8">
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
          className="mb-16"
        >
          <p className="mono text-[11px] tracking-[0.35em] text-[#00F0FF] mb-4" data-testid="credibility-eyebrow">
            CERTIFIED STANDARD // THE BACKBONE
          </p>
          <h2 className="font-display uppercase text-4xl sm:text-5xl lg:text-6xl leading-[0.95]" data-testid="credibility-heading">
            BACKED BY THE <span className="text-[#CCFF00]">REAL ONES</span>
          </h2>
        </motion.div>

        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-px bg-[#22222B]">
          {CREDS.map((c, i) => (
            <motion.div
              key={c.id}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-60px" }}
              transition={{ duration: 0.7, delay: i * 0.1 }}
              className="bg-[#08080A] p-6 sm:p-8 group hover:bg-[#111115] transition-colors duration-500"
              data-testid={`cred-${c.id}`}
            >
              <div className={`h-36 mb-8 overflow-hidden border border-[#22222B] ${c.contain ? "bg-black flex items-center justify-center p-3" : ""}`}>
                <img
                  src={c.img}
                  alt={c.title}
                  loading="lazy"
                  className={c.contain
                    ? "max-h-full max-w-full object-contain"
                    : `h-full w-full object-cover ${c.pos || "object-top"} grayscale group-hover:grayscale-0 transition-all duration-700`}
                />
              </div>
              <p className="mono text-[10px] tracking-[0.3em] text-[#60606E] mb-3">{c.kicker}</p>
              <h3 className="font-display uppercase text-lg sm:text-xl leading-tight mb-4">
                {c.url ? (
                  <a href={c.url} target="_blank" rel="noopener noreferrer" className="hover:text-[#00F0FF] transition-colors duration-300" data-testid={`cred-link-${c.id}`}>
                    {c.title}
                  </a>
                ) : (
                  c.title
                )}
              </h3>
              <p className="text-sm text-[#A0A0AB] leading-relaxed">{c.body}</p>
            </motion.div>
          ))}
        </div>

        <motion.a
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8, delay: 0.3 }}
          href="https://www.linkedin.com/in/aandrbeast"
          target="_blank"
          rel="noopener noreferrer"
          className="mt-10 inline-flex items-center gap-3 mono text-[11px] tracking-[0.25em] text-[#A0A0AB] hover:text-[#00F0FF] transition-colors duration-300"
          data-testid="credibility-linkedin-link"
        >
          <Linkedin className="h-4 w-4" />
          FOUNDER ON LINKEDIN — /IN/AANDRBEAST
        </motion.a>
      </div>
    </section>
  );
}
